import React, { useState, useEffect, useCallback } from 'react';
import {
  getFiscalStatus,
  getFiscalReceipts,
  fiscalNullbeleg,
  fiscalSchlussbeleg,
  fiscalStartbeleg,
  fiscalJahresbeleg,
} from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Shield, ShieldAlert, ShieldCheck, AlertTriangle, CheckCircle2,
  RefreshCw, Loader2, FileText, FlaskConical,
} from 'lucide-react';
import { toast } from 'sonner';

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDt(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('de-AT');
}

function receiptTypeBadge(type) {
  const map = {
    receipt:      { label: 'Kassenbeleg',   variant: 'secondary' },
    startbeleg:   { label: 'Startbeleg',    variant: 'outline'   },
    jahresbeleg:  { label: 'Jahresbeleg',   variant: 'outline'   },
    nullbeleg:    { label: 'Nullbeleg',     variant: 'secondary' },
    schlussbeleg: { label: 'Schlussbeleg',  variant: 'destructive' },
  };
  const { label, variant } = map[type] || { label: type, variant: 'secondary' };
  return <Badge variant={variant}>{label}</Badge>;
}

// ── TSE Status Card ────────────────────────────────────────────────────────────

function TseStatusCard({ status, onRefresh, onNullbeleg, loading }) {
  if (!status) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const { tse_ok, failure_since, mock_mode, cashbox_configured, last_signature_at } = status;

  let icon, title, description, colorClass;
  if (mock_mode || !cashbox_configured) {
    icon = <FlaskConical className="h-6 w-6 text-amber-500" />;
    title = 'Testmodus aktiv';
    description = mock_mode
      ? 'ft_is_mock ist aktiviert — Signaturen sind nicht rechtskonform.'
      : 'Keine CashBox ID konfiguriert.';
    colorClass = 'border-amber-200 bg-amber-50';
  } else if (failure_since) {
    icon = <ShieldAlert className="h-6 w-6 text-red-600" />;
    title = 'Signatureinrichtung ausgefallen';
    description = `Ausfall seit ${formatDt(failure_since)}`;
    colorClass = 'border-red-200 bg-red-50';
  } else if (tse_ok) {
    icon = <ShieldCheck className="h-6 w-6 text-emerald-600" />;
    title = 'TSE aktiv';
    description = last_signature_at
      ? `Letzte Signatur: ${formatDt(last_signature_at)}`
      : 'Bereit';
    colorClass = 'border-emerald-200 bg-emerald-50';
  } else {
    icon = <Shield className="h-6 w-6 text-muted-foreground" />;
    title = 'TSE nicht konfiguriert';
    description = 'Bitte CashBox-Daten in den Einstellungen hinterlegen.';
    colorClass = '';
  }

  return (
    <Card className={colorClass}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          {icon}
          TSE-Status
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <p className="font-medium">{title}</p>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading}>
            <RefreshCw className={`h-3 w-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </Button>
          {failure_since && (
            <Button variant="destructive" size="sm" onClick={onNullbeleg} disabled={loading}>
              Nullbeleg senden
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Fiscal Actions Card ────────────────────────────────────────────────────────

function FiscalActionsCard({ onAction, loading }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Fiskalbelege
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-3">
          Pflichtbelege gemäß RKSV. Nur bei Ersteinrichtung oder Jahresabschluss benötigt.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => onAction('startbeleg')} disabled={loading}>
            Startbeleg
          </Button>
          <Button variant="outline" size="sm" onClick={() => onAction('jahresbeleg')} disabled={loading}>
            Jahresbeleg
          </Button>
          <Button variant="outline" size="sm" onClick={() => onAction('nullbeleg')} disabled={loading}>
            Nullbeleg
          </Button>
          <Button variant="outline" size="sm" onClick={() => onAction('schlussbeleg')} disabled={loading}>
            Schlussbeleg
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Receipt History Table ─────────────────────────────────────────────────────

function ReceiptHistoryTable({ receipts, onLoadMore, hasMore, loading }) {
  const rows = Array.isArray(receipts) ? receipts : [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="h-4 w-4" />
          Belegjournal
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Beleg-Nr.</th>
                <th className="px-4 py-2 text-left font-medium">Typ</th>
                <th className="px-4 py-2 text-right font-medium">Betrag</th>
                <th className="px-4 py-2 text-left font-medium">Signatur</th>
                <th className="px-4 py-2 text-left font-medium">Zeitstempel</th>
                <th className="px-4 py-2 text-left font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    Noch keine Fiskalbelege vorhanden.
                  </td>
                </tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-b hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-2 font-mono text-xs">{r.receipt_number}</td>
                  <td className="px-4 py-2">{receiptTypeBadge(r.receipt_type)}</td>
                  <td className="px-4 py-2 text-right">
                    {r.receipt_type === 'receipt' ? `€ ${(r.total || 0).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-2">
                    <SignatureCell signature={r.ft_signature} hash={r.ft_hash} />
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {formatDt(r.created_at)}
                  </td>
                  <td className="px-4 py-2">
                    <ReceiptStatusBadge receipt={r} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className="p-4 flex justify-center">
            <Button variant="ghost" size="sm" onClick={onLoadMore} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Mehr laden'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SignatureCell({ signature, hash }) {
  if (!signature) return <span className="text-muted-foreground">—</span>;
  const short = signature.length > 24 ? `${signature.slice(0, 12)}…${signature.slice(-8)}` : signature;
  return (
    <span className="font-mono text-xs" title={`${signature}\nHash: ${hash || ''}`}>
      {short}
    </span>
  );
}

function ReceiptStatusBadge({ receipt }) {
  if (receipt.is_failure_mode) {
    return (
      <Badge variant="destructive" className="text-xs gap-1">
        <AlertTriangle className="h-3 w-3" />
        Ausfall
      </Badge>
    );
  }
  if (receipt.is_mock) {
    return (
      <Badge variant="secondary" className="text-xs gap-1">
        <FlaskConical className="h-3 w-3" />
        Mock
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs gap-1 border-emerald-300 text-emerald-700">
      <CheckCircle2 className="h-3 w-3" />
      Signiert
    </Badge>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 50;

export default function AdminFiscal() {
  const [status, setStatus] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const data = await getFiscalStatus();
      setStatus(data);
    } catch (err) {
      toast.error('TSE-Status konnte nicht geladen werden');
    }
  }, []);

  const loadReceipts = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const response = await getFiscalReceipts({ page: p, page_size: PAGE_SIZE });
      const data = Array.isArray(response) ? response : Array.isArray(response?.items) ? response.items : [];

      if (p === 1) {
        setReceipts(data);
      } else {
        setReceipts(prev => [...prev, ...data]);
      }
      const total = typeof response?.total === 'number' ? response.total : null;
      setHasMore(total !== null ? p * PAGE_SIZE < total : data.length === PAGE_SIZE);
      setPage(p);
    } catch (err) {
      toast.error('Belegliste konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
    loadReceipts(1);
  }, [loadStatus, loadReceipts]);

  const handleAction = async (type) => {
    setActionLoading(true);
    const actionMap = {
      startbeleg:   fiscalStartbeleg,
      jahresbeleg:  fiscalJahresbeleg,
      nullbeleg:    fiscalNullbeleg,
      schlussbeleg: fiscalSchlussbeleg,
    };
    try {
      const result = await actionMap[type]();
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} erstellt`, {
        description: `Beleg-Nr.: ${result.receipt_number}`,
      });
      await loadStatus();
      await loadReceipts(1);
    } catch (err) {
      toast.error(`Fehler beim Erstellen des ${type}`);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Fiskalierung (RKSV)</h1>
        <p className="text-muted-foreground mt-1">
          Fiskaltrust.at TSE-Integration — österreichische Registrierkassenpflicht
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TseStatusCard
          status={status}
          onRefresh={loadStatus}
          onNullbeleg={() => handleAction('nullbeleg')}
          loading={actionLoading}
        />
        <FiscalActionsCard onAction={handleAction} loading={actionLoading} />
      </div>

      <ReceiptHistoryTable
        receipts={receipts}
        onLoadMore={() => loadReceipts(page + 1)}
        hasMore={hasMore}
        loading={loading}
      />
    </div>
  );
}
